import ReactMarkdown from 'react-markdown';
import type { ChatMessage, AssistantMessageMetadata } from '../../types';
import styles from './MessageBubble.module.css';

interface MessageBubbleProps {
  message: ChatMessage;
}

/** Pull any <thinking>...</thinking> sections out of assistant text.
 * Returns the extracted reasoning blocks and the answer with the tags removed.
 * The literal word "thinking" / the tags themselves are never shown to the user. */
function splitThinking(raw: string): { thinking: string[]; answer: string } {
  const thinking: string[] = [];
  const answer = raw
    .replace(/<thinking>([\s\S]*?)<\/thinking>/gi, (_match, inner: string) => {
      const text = inner.trim();
      if (text) thinking.push(text);
      return '';
    })
    .trim();
  return { thinking, answer };
}

export function MessageBubble({ message }: MessageBubbleProps) {
  const isUser = message.role === 'user';
  const time = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(new Date(message.createdAt));

  const { thinking, answer } = isUser
    ? { thinking: [], answer: message.content }
    : splitThinking(message.content);

  const athenaQueries =
    (message.metadata as AssistantMessageMetadata | undefined)?.athenaQueries ?? [];

  return (
    <div
      className={`${styles.wrapper} ${isUser ? styles.user : styles.assistant}`}
      role="article"
      aria-label={`${isUser ? 'You' : 'Assistant'}: ${message.content.slice(0, 60)}`}
    >
      {!isUser && (
        <div className={styles.avatar} aria-hidden="true">
          <AssistantIcon />
        </div>
      )}

      <div className={styles.bubble}>
        {isUser ? (
          <p className={styles.userText}>{message.content}</p>
        ) : (
          <>
            {thinking.map((block, i) => (
              <div
                key={`thinking-${i}`}
                className={styles.thinking}
                role="note"
                aria-label="Assistant reasoning"
              >
                {block}
              </div>
            ))}

            <div className="markdown-content">
              <ReactMarkdown>{answer}</ReactMarkdown>
            </div>

            {athenaQueries.map((sql, i) => (
              <figure key={`query-${i}`} className={styles.queryBox}>
                <figcaption className={styles.queryLabel}>
                  {athenaQueries.length > 1 ? `Athena query ${i + 1}` : 'Athena query'}
                </figcaption>
                <pre className={styles.queryCode}>
                  <code>{sql}</code>
                </pre>
              </figure>
            ))}
          </>
        )}
        <time
          className={styles.timestamp}
          dateTime={message.createdAt}
          aria-label={`Sent at ${time}`}
        >
          {time}
        </time>
      </div>

      {isUser && (
        <div className={`${styles.avatar} ${styles.userAvatar}`} aria-hidden="true">
          <UserIcon />
        </div>
      )}
    </div>
  );
}

function AssistantIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <rect width="32" height="32" rx="8" fill="#00AEBD" />
      <path
        d="M8 10h3l3 8 3-8h2l3 8 3-8h3l-4.5 12h-2.5l-2.5-7-2.5 7h-2.5L8 10z"
        fill="white"
      />
    </svg>
  );
}

function UserIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}
